import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, Mail, Home, BookOpen } from 'lucide-react';
import { getPaymentInfo } from '../services/mercadopago';
import copy from '../copy';

interface PaymentInfo {
  id: string;
  status: string;
  status_detail: string;
  external_reference: string;
  amount: number;
  currency: string;
  payment_method: string;
  created_at: string;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');
  // const externalReference = searchParams.get('external_reference');

  const formatPrice = (price: number) => `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!paymentId) {
        setError('ID de pago no encontrado');
        setLoading(false);
        return;
      }

      try {
        const payment = await getPaymentInfo(paymentId);
        setPaymentInfo({
          id: payment.id,
          status: payment.status,
          status_detail: payment.status_detail,
          external_reference: payment.external_reference,
          amount: payment.transaction_amount,
          currency: payment.currency_id,
          payment_method: payment.payment_method?.type || 'Desconocido',
          created_at: payment.date_created,
        });
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError('Error al cargar la información del pago');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [paymentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <CheckCircle className="w-8 h-8 text-accent-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">Procesando Pago...</h1>
          <p className="text-primary-600">Por favor espera mientras confirmamos tu pago</p>
        </div>
      </div>
    );
  }

  if (error || !paymentInfo) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">{copy.payment_failure_titulo}</h1>
          <p className="text-primary-600 mb-6">{error || 'No se pudo procesar la información del pago'}</p>
          <Link to="/carrito" className="btn-primary">
            Volver al Carrito
          </Link>
        </div>
      </div>
    );
  }

  const isPaymentSuccessful = status === 'approved' || paymentInfo.status === 'approved';

  if (!isPaymentSuccessful) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">{copy.payment_pending_titulo}</h1>
          <p className="text-primary-600 mb-6">
            Tu pago está siendo procesado. Recibirás un email de confirmación una vez que se complete.
          </p>
          <Link to="/" className="btn-primary">
            Volver al Inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 section-padding">
      <div className="container-max">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-900 mb-4">
              ¡Pago Exitoso!
            </h1>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              Gracias por tu compra. Tus cursos ya están disponibles en tu cuenta.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Payment Details */}
            <div className="card-minimal">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                Detalles del Pago
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-primary-600">ID de Pago:</span>
                  <span className="font-medium text-primary-900">{paymentInfo.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-600">Monto:</span>
                  <span className="font-semibold text-accent-600">
                    {formatPrice(paymentInfo.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-600">Método de Pago:</span>
                  <span className="font-medium text-primary-900 capitalize">
                    {paymentInfo.payment_method}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-600">Estado:</span>
                  <span className="font-medium text-green-600 capitalize">
                    Aprobado
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-600">Fecha:</span>
                  <span className="font-medium text-primary-900">
                    {new Date(paymentInfo.created_at).toLocaleDateString('es-MX')}
                  </span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="card-minimal">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                ¿Qué sigue?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Mail className="w-4 h-4 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Confirmación por Email</h3>
                    <p className="text-sm text-primary-600">
                      Recibirás un email de confirmación con los detalles de acceso a tus cursos
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Download className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Acceso a Cursos</h3>
                    <p className="text-sm text-primary-600">
                      Tus cursos ya están disponibles en tu panel de aprendizaje
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <BookOpen className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Comienza a Aprender</h3>
                    <p className="text-sm text-primary-600">
                      Inicia tu viaje de aprendizaje con acceso de por vida a tus cursos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center mt-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard" className="btn-primary inline-flex items-center">
                <BookOpen className="mr-2 w-5 h-5" />
                Ir al Panel
              </Link>
              <Link to="/" className="btn-secondary inline-flex items-center">
                <Home className="mr-2 w-5 h-5" />
                Volver al Inicio
              </Link>
            </div>
            
            <p className="text-sm text-primary-500">
              ¿Necesitas ayuda? Contacta a nuestro equipo de soporte en{' '}
              <a href="mailto:support@iberotech.org" className="text-accent-600 hover:underline">
                support@iberotech.org
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess; 